import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { fetchArticles } from "../lib/api";

const gradients = {
  navy: "linear-gradient(135deg, #0A1F47 0%, #1C3D7A 100%)",
  blue: "linear-gradient(135deg, #0A1F47 0%, #2E6BF6 100%)",
  deep: "linear-gradient(135deg, #061433 0%, #0A1F47 100%)",
};

export default function ThoughtLeadership() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles({ limit: 6 })
      .then((d) => setArticles(d))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="insights" className="nst-light py-24 lg:py-32 border-t border-[#D9E0F0]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <span className="pill-label">
              <BookOpen className="w-3.5 h-3.5" />
              Thought Leadership
            </span>
            <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0A1F47] leading-[1.05]">
              Engineering <span className="text-[#2E6BF6]">Insights</span>
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-96 bg-white/60 rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {articles.map((article, i) => (
              <Link
                key={article.slug}
                to={`/insights/${article.slug}`}
                className="group bg-white border border-[#E2E8F4] rounded-3xl overflow-hidden card-lift hover:border-[#2E6BF6]/40 block"
              >
                <div
                  className="relative h-48 overflow-hidden"
                  style={{ background: gradients[article.gradient_key] || gradients.navy }}
                >
                  <div className="absolute inset-0 bg-grid-dark opacity-40" />
                  <div className="absolute bottom-4 left-5">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold backdrop-blur-sm border border-white/20">
                      {article.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 text-white/40 text-7xl font-black leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="text-xl font-bold text-[#0A1F47] leading-snug group-hover:text-[#2E6BF6] transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#0A1F47]/70 leading-relaxed">{article.excerpt}</p>
                  <div className="mt-5 pt-5 border-t border-[#E2E8F4] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-[#0A1F47]/60 font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      {article.read_time}
                    </div>
                    <span className="text-[#2E6BF6] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
